import { useRef, useState } from 'react';
import { Panel } from '../../../components/panels/Panel';
import { formatSecondsToMinutesSeconds } from '../../../helpers/date/date-helper';
import { CameraIcon, PaintBrushIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { patchData } from '../../../services/api/api-tools';
import { ActionButton } from "../../../components/buttons/ActionButton";

export const ShortsTimeline = ({ selectedRecord, recordAnnotations, setRecordAnnotations, duration, currentTime }) => {
  if (!duration) return null;

  const timelineRef = useRef(null);
  const [dragState, setDragState] = useState(null);

  const ticksNumber = Math.min(Math.ceil(duration / 30), 10);
  const tickInterval = duration / ticksNumber;
  const progressPercent = Math.min(Math.max(currentTime / duration * 100, 0), 100);

  const getTimelineWidth = () => timelineRef.current?.getBoundingClientRect().width ?? 1;

  const clampAnnotation = (annotation) => {
    const start = Math.max(0, Math.min(annotation.start, duration));
    const end = Math.max(start + 1, Math.min(annotation.end, duration));

    return {
      ...annotation,
      start,
      end
    };
  };

  const updateAnnotation = (annotationId, updater) => {
    setRecordAnnotations((prev) => prev.map((annotation) => (
      annotation.id === annotationId
        ? clampAnnotation(updater(annotation))
        : annotation
    )));
  };

  const saveAnnotations = async (annotations) => {
    const savedAnnotations = await patchData(
      `records/${selectedRecord.id}/annotations`,
      annotations
    );

    setRecordAnnotations(savedAnnotations);
  };

  const handleAnnotationPointerDown = (e, annotation, action) => {
    e.stopPropagation();

    const timelineWidth = getTimelineWidth();

    e.currentTarget.setPointerCapture(e.pointerId);

    setDragState({
      action,
      annotationId: annotation.id,
      pointerId: e.pointerId,
      initialX: e.clientX,
      initialStart: annotation.start,
      initialEnd: annotation.end,
      timelineWidth
    });
  };

  const handleAnnotationPointerMove = (e) => {
    if (!dragState) return;

    e.stopPropagation();

    const deltaX = e.clientX - dragState.initialX;
    const deltaSeconds = deltaX / dragState.timelineWidth * duration;

    updateAnnotation(dragState.annotationId, (annotation) => {
      if (dragState.action === 'move') {
        const annotationDuration = dragState.initialEnd - dragState.initialStart;
        const nextStart = Math.max(0, Math.min(dragState.initialStart + deltaSeconds, duration - annotationDuration));

        return {
          ...annotation,
          start: nextStart,
          end: nextStart + annotationDuration
        };
      }

      if (dragState.action === 'resize-start') {
        return {
          ...annotation,
          start: Math.max(0, Math.min(dragState.initialStart + deltaSeconds, dragState.initialEnd - 1))
        };
      }

      return {
        ...annotation,
        end: Math.max(dragState.initialStart + 1, Math.min(dragState.initialEnd + deltaSeconds, duration))
      };
    });
  };

  const handleAnnotationPointerUp = async (e) => {
    if (!dragState) return;

    e.stopPropagation();

    const savedAnnotations = recordAnnotations.map((annotation) => (
      annotation.id === dragState.annotationId
        ? clampAnnotation(annotation)
        : annotation
    ));

    setDragState(null);
    await saveAnnotations(savedAnnotations);
  };

  const handleDeleteAnnotation = async (annotationId) => {
    console.log(annotationId);
    const newRecordAnnotations = recordAnnotations.filter((annotation) => annotation.id !== annotationId);

    setRecordAnnotations(newRecordAnnotations);

    const savedAnnotations = await patchData(
      `records/${selectedRecord.id}/annotations`,
      newRecordAnnotations
    );

    setRecordAnnotations(savedAnnotations);
  };

  return (
    <Panel height="h-[40%]" category="secondary" noPadding={true} rounded={true} noHeader={true}>
      <div className="h-full flex text-sm">

        {/* Left part */}
        <div className="w-36 border-r-2 border-perception-black-800">
          <div className="h-12 border-b-2 border-perception-black-800"/>

          <div className="h-12 flex items-center gap-2 border-b-2 border-perception-black-800 px-2">
            <CameraIcon className="size-4"/>
            Short video
          </div>

          <div className="h-12 flex items-center gap-2 px-2">
            <PaintBrushIcon className="size-4"/>
            Annotations
          </div>
        </div>

        {/* Right part */}
        <div className="flex-1 flex flex-col relative">
          {/* Vertical time bar */}
          <div className="absolute top-0 bottom-0 left-1 right-1 pointer-events-none">
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-perception-blue z-20 pointer-events-none transition-all duration-100 ease-linear"
              style={{ left: `${progressPercent}%` }}
            />
          </div>

          {/* Ticks */}
          <div className="h-12 border-b-2 border-perception-black-800 px-1">
            <div className="h-full w-full relative">
              {Array.from({ length: ticksNumber + 1 }).map((_, tickIndex) => {
                const tickTime = tickIndex * tickInterval;
                const tickPosition = (tickTime / duration) * 100;
                const tickTranslate = tickIndex === 0 ? 0 : tickIndex === ticksNumber ? -100 : -50;
                const halfTickPosition = (tickTime + tickInterval / 2) / duration * 100;

                return (
                  <div key={tickIndex} className="inline-block select-none" style={{ width: `${100 / ticksNumber}%` }}>
                    <div className="absolute border-l border-perception-gray-500" style={{ height: '10px', left: `${tickPosition}%`, top: '5px' }}/>

                    {halfTickPosition <= 100 && (
                      <div className="absolute border-l border-perception-gray-500" style={{ height: '5px', left: `${halfTickPosition}%`, top: '5px' }}/>
                    )}

                    <div className="text-xs text-perception-gray-500" style={{ position: 'absolute', left: `${tickPosition}%`, top: '20px', transform: `translateX(${tickTranslate}%)` }}>
                      {formatSecondsToMinutesSeconds(tickTime)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="h-12 border-b-2 border-perception-black-800 px-1">
            <div className="h-full w-full flex items-center relative">
              <div className="h-8 bg-perception-blue rounded-md text-white flex items-center px-2 text-nowrap overflow-hidden select-none" style={{ width: '100%' }}>
                Short video
              </div>
            </div>
          </div>

          {/* Annotations */}
          <div className="overflow-y-auto flex-1" ref={timelineRef}>
            {
              recordAnnotations.map((annotation, annotationIndex) => {
                const startPercent = annotation.start / duration * 100;
                const widthPercent = annotation.end / duration * 100 - startPercent;

                return (
                  <div key={annotation.id} className="h-10 border-b-2 border-perception-black-800 px-1">
                    <div className="h-full w-full flex items-center relative">
                      <div
                        className="h-6 text-white absolute rounded-md flex items-center px-2 text-nowrap overflow-hidden select-none cursor-grab active:cursor-grabbing"
                        style={{ left: startPercent + '%', width: widthPercent + '%', background: annotation.stroke_color }}
                        onPointerDown={(e) => handleAnnotationPointerDown(e, annotation, 'move')}
                        onPointerMove={handleAnnotationPointerMove}
                        onPointerUp={handleAnnotationPointerUp}
                      >
                        <div
                          className="absolute top-0 left-0 bottom-0 w-2 cursor-ew-resize z-10"
                          onPointerDown={(e) => handleAnnotationPointerDown(e, annotation, 'resize-start')}
                        />

                        <button
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAnnotation(annotation.id);
                          }}
                          className="mr-1 hover:bg-danger rounded-full"
                        >
                          <XMarkIcon className="size-4 text-white"/>
                        </button>

                        Annotation #{annotationIndex + 1}

                        <div
                          className="absolute top-0 right-0 bottom-0 w-2 cursor-ew-resize z-10"
                          onPointerDown={(e) => handleAnnotationPointerDown(e, annotation, 'resize-end')}
                        />

                        <div className="absolute top-0 left-0 right-0 bottom-0 border-y-1 border-x-4 border-white/50 pointer-events-none"/>
                      </div>
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>

      </div>
    </Panel>
  );
};